// Client Portal: the customer's OPEN work (Session AUD, email system Phase 5, Item 3).
//
// "Has an engineer been booked, and when?" was the question customers emailed most after a fault. This
// answers it on the page: every open call-out, repair and booked service visit, what it is, where, its
// stage in plain words, the booked day and the attending engineer's first name. The server builds the
// rows from an explicit allow-list (portalOpenWork.ts); nothing here adds to it. No price, no notes, no
// access details.
//
// Two surfaces share the one list: a short section on the Overview (OpenWorkSection) and the full page
// at /portal/work. The Overview's section never breaks the Overview: if the read fails it says nothing.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { PortalOpenWorkRow } from "../../lib/api";
import { PortalShell } from "./PortalShell";
import { ShowAllList } from "./ShowAllList";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";

// The stage's colour, from the locked palette only: amber while we are still arranging something, the
// brand green once a person or a date is fixed, the technical blue for the office's own check.
function stageTone(label: string): string {
  if (label.startsWith("Received") || label.startsWith("On hold")) return "text-amber-400";
  if (label.startsWith("Finished")) return "text-ele-loler";
  return "text-ele-green";
}

export function OpenWorkList({ rows, cap }: { rows: PortalOpenWorkRow[]; cap?: number }) {
  const shown = cap === undefined ? rows : rows.slice(0, cap);
  return (
    <ul className="overflow-hidden rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)]">
      {shown.map((r) => (
        <li key={r.key} className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 px-4 py-3 last:border-0">
          <div className="min-w-0">
            <p className="text-sm text-ele-white">
              {r.kindLabel} <span className="text-ele-body">· {r.siteName}</span>
            </p>
            <p className="text-xs text-ele-body">{r.liftRef}</p>
          </div>
          <div className="text-right text-xs">
            <p className={`font-menu tracking-wide ${stageTone(r.statusLabel)}`}>{r.statusLabel}</p>
            <p className="text-ele-body">
              {r.bookedDateDisplay ? `Booked for ${r.bookedDateDisplay}` : "Date to be confirmed"}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// The Overview's short section. Up to five rows, a link to the rest, and the way to report a fault.
// Renders nothing until the read lands, and nothing at all if it fails: the Overview's own content
// must never wait on, or be broken by, this one read.
export const OVERVIEW_OPEN_WORK_CAP = 5;

export function OpenWorkSection() {
  const [rows, setRows] = useState<PortalOpenWorkRow[] | null>(null);
  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => api.portal.openWork())
      .then((r) => {
        if (live) setRows(r.rows);
      })
      .catch(() => {
        // Silent on the Overview by design; the full page reports a failure.
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <section className="mb-8" aria-labelledby="portal-open-work">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="portal-open-work" className="font-heading text-xl text-ele-purple">
          Work in progress
        </h2>
        <div className="flex items-center gap-4">
          {rows && rows.length > OVERVIEW_OPEN_WORK_CAP && (
            <Link to="/portal/work" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
              See all ({rows.length})
            </Link>
          )}
          <Link to="/portal/report-fault" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
            Report a fault
          </Link>
        </div>
      </div>
      {rows === null ? null : rows.length === 0 ? (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
          Nothing is open on your lifts at the moment.
        </p>
      ) : (
        <OpenWorkList rows={rows} cap={OVERVIEW_OPEN_WORK_CAP} />
      )}
    </section>
  );
}

// The full page, /portal/work.
export function PortalOpenWorkPage() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<PortalOpenWorkRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([api.portal.me(), api.portal.openWork()])
      .then(([me, work]) => {
        if (!live) return;
        setUserName(me.customer.name);
        setRows(work.rows);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!rows) return <FullScreenLoader />;

  return (
    <PortalShell wide userName={userName} onSignOut={logout}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-wide text-ele-white">Work in progress</h1>
          <p className="mt-1 text-ele-body">Call-outs, repairs and booked service visits on your lifts that are not finished yet.</p>
        </div>
        <Link
          to="/portal/report-fault"
          className="inline-flex min-h-11 items-center rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg"
        >
          Report a fault
        </Link>
      </header>
      {rows.length === 0 ? (
        <p className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
          Nothing is open on your lifts at the moment.
        </p>
      ) : (
        <ShowAllList count={rows.length} render={(cap) => <OpenWorkList rows={rows} cap={cap} />} />
      )}
      <p className="mt-4 text-xs text-ele-body">
        Service visits are shown here once they are booked within the next month. Finished work moves to your history.
      </p>
    </PortalShell>
  );
}
